import {
  mealsSellSelectedMeals,
  mealsSellMealsSsrSuccess,
} from '@actions/meals.action';
import { Injectable } from '@angular/core';
import { MealServiceType, MealsSsrEnum } from '@constants/meals.constants';
import {
  N0PassengerSsrsUpdate,
  N0SsrPassengerUpdateRequest,
  N0SsrUpdate,
} from '@models/requests/ssr-passenger-update-request.model';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { selectBookingMeals } from '@selectors/booking.selector';
import { selectMealSelectionsState } from '@selectors/meals.selector';
import { SsrService } from '@services/api-clients/ssr.service';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

@Injectable()
export class MealEffect {
  constructor(
    private actions$: Actions,
    private store: Store,
    private ssrService: SsrService,
  ) {}

  sellMealsSsrs$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(mealsSellSelectedMeals),
      concatLatestFrom(() => [
        this.store.select(selectMealSelectionsState),
        this.store.select(selectBookingMeals),
      ]),
      mergeMap(([action, mealsSelected, mealsInBooking]) => {
        const payload: N0SsrPassengerUpdateRequest[] = [];

        const anyMealsSelected =
          mealsSelected?.some((ms) =>
            ms.passengers.some((p) => p.ssrs.length > 0),
          ) ?? false;

        const anyMealsExistsInBooking =
          mealsInBooking?.some((mib) =>
            mib.passengerMeals.some(
              (pm) => pm.firstService.length > 0 || pm.secondService.length > 0,
            ),
          ) ?? false;

        /* istanbul ignore else */
        if (anyMealsExistsInBooking) {
          mealsInBooking?.forEach((mib) => {
            const passengerSsrUpdate: N0PassengerSsrsUpdate[] = [];
            mib.passengerMeals.forEach((pmib) => {
              const currentMealsPassengerSsrUpdate = mealsSelected
                ?.find((ms) => ms.legKey === mib.legKey)
                ?.passengers.find((p) => p.passengerKey === pmib.passengerKey);
              const selectedFirstServiceSsr = (
                currentMealsPassengerSsrUpdate?.ssrs
                  .find((s) => s.serviceType === MealServiceType.FIRST_SERVICE)
                  ?.ssrCode?.trim() ?? ''
              ).toUpperCase() as MealsSsrEnum;
              const selectedSecondServiceSsr = (
                currentMealsPassengerSsrUpdate?.ssrs
                  .find((s) => s.serviceType === MealServiceType.SECOND_SERVICE)
                  ?.ssrCode?.trim() ?? ''
              ).toUpperCase() as MealsSsrEnum;
              const ssrsUpdate: N0SsrUpdate[] = [];

              /* istanbul ignore else */
              if (pmib.firstService.length > 0) {
                const bookingFirstServicSsr = (
                  pmib.firstService[0]?.ssrCode?.trim() ?? ''
                ).toUpperCase() as MealsSsrEnum;
                if (selectedFirstServiceSsr.length === 0) {
                  ssrsUpdate.push({ ssrCode: bookingFirstServicSsr, count: 0 });
                } else if (selectedFirstServiceSsr !== bookingFirstServicSsr) {
                  ssrsUpdate.push({
                    ssrCode: selectedFirstServiceSsr,
                    count: 1,
                  });
                  ssrsUpdate.push({ ssrCode: bookingFirstServicSsr, count: 0 });
                }
              }

              /* istanbul ignore else */
              if (
                pmib.firstService.length === 0 &&
                selectedFirstServiceSsr.length > 0
              ) {
                ssrsUpdate.push({ ssrCode: selectedFirstServiceSsr, count: 1 });
              }

              /* istanbul ignore else */
              if (pmib.secondService.length > 0) {
                const bookingSecondServicSsr = (
                  pmib.secondService[0]?.ssrCode?.trim() ?? ''
                ).toUpperCase() as MealsSsrEnum;
                if (selectedSecondServiceSsr.length === 0) {
                  ssrsUpdate.push({
                    ssrCode: bookingSecondServicSsr,
                    count: 0,
                  });
                } else if (
                  selectedSecondServiceSsr !== bookingSecondServicSsr
                ) {
                  ssrsUpdate.push({
                    ssrCode: selectedSecondServiceSsr,
                    count: 1,
                  });
                  ssrsUpdate.push({
                    ssrCode: bookingSecondServicSsr,
                    count: 0,
                  });
                }
              }

              /* istanbul ignore else */
              if (
                pmib.secondService.length === 0 &&
                selectedSecondServiceSsr.length > 0
              ) {
                ssrsUpdate.push({
                  ssrCode: selectedSecondServiceSsr,
                  count: 1,
                });
              }

              /* istanbul ignore else */
              if (ssrsUpdate.length > 0) {
                passengerSsrUpdate.push({
                  passengerKey: pmib.passengerKey,
                  ssrs: [...ssrsUpdate],
                });
              }
            });

            /* istanbul ignore else */
            if (passengerSsrUpdate.length > 0) {
              payload.push({
                legKey: mib.legKey,
                passengers: passengerSsrUpdate,
              });
            }
          });
        }

        /* istanbul ignore else */
        if (!anyMealsExistsInBooking && anyMealsSelected) {
          mealsSelected?.forEach((ms) => {
            const passengerSsrUpdate: N0PassengerSsrsUpdate[] = [];
            ms.passengers.forEach((msp) => {
              const arrSsrsUpdate: N0SsrUpdate[] = [];
              msp.ssrs.forEach((s) =>
                arrSsrsUpdate.push({
                  ssrCode: s.ssrCode,
                  count: s.count,
                }),
              );

              /* istanbul ignore else */
              if (arrSsrsUpdate.length > 0) {
                passengerSsrUpdate.push({
                  passengerKey: msp.passengerKey,
                  ssrs: [...arrSsrsUpdate],
                });
              }
            });

            /* istanbul ignore else */
            if (passengerSsrUpdate.length > 0) {
              payload.push({
                legKey: ms.legKey,
                passengers: passengerSsrUpdate,
              });
            }
          });
        }

        if (payload.length > 0) {
          return this.ssrService
            .ssrUpdate(payload)
            .pipe(map(() => mealsSellMealsSsrSuccess({redirectToPayment: action.redirectToPayment})));
        } else {
          return of(mealsSellMealsSsrSuccess({redirectToPayment: action.redirectToPayment}));
        }
      }),
    );
  });
}
